import { Listbox } from '@headlessui/react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { Tooltip } from '@/components/Tooltip';
import { getQueryEntity } from '@/sections/insights/queryBuilder/entities';
import {
  getAllowedRelationships,
  getEdgeDataFromLabel,
  normalizeActual,
} from '@/sections/insights/queryBuilder/entities/relationships';
import type { RelationshipLabel } from '@/sections/insights/queryBuilder/entities/relationships/types';
import type {
  FlatGraphEdge,
  FlatGraphNode,
} from '@/sections/insights/queryBuilder/types/FlatGraph';
import type { Relationship } from '@/types';

interface EdgeLabelProps {
  edge: FlatGraphEdge;
  sourceNode: FlatGraphNode;
  targetNode: FlatGraphNode;
  isExpanded: boolean;
  editable: boolean;
  filterCount: number;
  hasFilters: boolean;
  onToggle: (edgeId: string) => void;
  onUpdateEdgeData?: (
    edgeId: string,
    data: Partial<FlatGraphEdge['data']>
  ) => void;
}

/**
 * EdgeLabel - Displays edge icon and label (editable select or display)
 */
export const EdgeLabel = ({
  edge,
  sourceNode,
  targetNode,
  isExpanded,
  editable,
  filterCount,
  hasFilters,
  onToggle,
  onUpdateEdgeData,
}: EdgeLabelProps) => {
  // Get allowed relationships based on source/target node labels
  const allowedRelationships = getAllowedRelationships(
    sourceNode.data.labels?.[0] ?? '',
    targetNode.data.labels?.[0] ?? ''
  );

  // Check if relationships are available
  const isDisabled = allowedRelationships.length === 0;

  // Get display names for source and target nodes
  const sourceLabel = sourceNode.data.labels?.[0] ?? '';
  const targetLabel = targetNode.data.labels?.[0] ?? '';

  const sourceEntity = getQueryEntity(sourceLabel);
  const targetEntity = getQueryEntity(targetLabel);

  const sourceDisplayName =
    (sourceEntity?.primaryLabel.display ??
      sourceEntity?.primaryLabel.actual ??
      sourceLabel) ||
    'source';
  const targetDisplayName =
    (targetEntity?.primaryLabel.display ??
      targetEntity?.primaryLabel.actual ??
      targetLabel) ||
    'target';

  // Handle both string and array labels from edge data
  const edgeLabels = Array.isArray(edge.data.label)
    ? edge.data.label
    : [edge.data.label];

  // Build lookup: actual value → RelationshipLabel (for display resolution)
  const actualToRelLabel = new Map<string, RelationshipLabel>();
  for (const rel of allowedRelationships) {
    for (const a of normalizeActual(rel)) {
      actualToRelLabel.set(a, rel);
    }
  }

  // Resolve current edge labels to their display names (deduplicated)
  const selectedDisplayNames = [
    ...new Set(
      edgeLabels
        .map(label => actualToRelLabel.get(label)?.display)
        .filter(Boolean)
    ),
  ] as string[];

  // Condensed display format
  const displayText = isDisabled
    ? 'No relationships available'
    : selectedDisplayNames.length === 0
      ? 'Select relationship...'
      : selectedDisplayNames.length > 2
        ? `${selectedDisplayNames[0]}, +${selectedDisplayNames.length - 1}`
        : selectedDisplayNames.join(', ');

  // Build lookup: display name → RelationshipLabel (for selection handling)
  const displayToRelLabel = new Map<string, RelationshipLabel>(
    allowedRelationships.map(rel => [rel.display, rel])
  );

  /**
   * Handle label selection change.
   * Listbox value is display names (since one display can map to multiple actuals).
   * We expand to actual values and auto-apply edge data from the label.
   */
  const handleLabelsChange = (newDisplayNames: string[]) => {
    // Expand display names to actual relationship types
    const allActuals: string[] = [];
    let selectedLabel: RelationshipLabel | undefined;

    for (const displayName of newDisplayNames) {
      const rel = displayToRelLabel.get(displayName);
      if (rel) {
        allActuals.push(...normalizeActual(rel));
        // Use the last selected label for edge data (in multi-select,
        // labels with different edge properties are unusual)
        selectedLabel = rel;
      }
    }

    // Auto-apply edge data from the selected label
    if (onUpdateEdgeData) {
      if (selectedLabel) {
        const edgeData = getEdgeDataFromLabel(selectedLabel);
        onUpdateEdgeData(edge.id, {
          label: allActuals as Relationship[],
          label_operator: edgeData.label_operator,
          length: edgeData.length,
          optional: edgeData.optional,
          not_exists: edgeData.not_exists,
        });
      } else {
        // All labels deselected — reset to empty state
        onUpdateEdgeData(edge.id, {
          label: [] as Relationship[],
        });
      }
    }
  };

  return (
    <div
      onClick={hasFilters ? () => onToggle(edge.id) : undefined}
      className={`flex w-full items-center gap-3 rounded-md p-3 text-left ${hasFilters ? 'hover:bg-chariot-background-secondary cursor-pointer' : ''}`}
      data-testid={`edge-card-${edge.id}`}
      role={hasFilters ? 'button' : undefined}
      tabIndex={hasFilters ? 0 : undefined}
      onKeyDown={
        hasFilters
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle(edge.id);
              }
            }
          : undefined
      }
      aria-expanded={hasFilters ? isExpanded : undefined}
      aria-controls={hasFilters ? `edge-content-${edge.id}` : undefined}
      aria-label={`${displayText} relationship${hasFilters ? ` with ${filterCount} filter${filterCount !== 1 ? 's' : ''}` : ''}`}
    >
      <div className="flex-1 pl-12">
        {editable ? (
          <Listbox
            value={selectedDisplayNames}
            onChange={handleLabelsChange}
            multiple
            disabled={isDisabled}
          >
            <div className="relative">
              {isDisabled ? (
                <Tooltip
                  title={`No relationship types exist between ${sourceDisplayName} and ${targetDisplayName}. Please select different node labels.`}
                >
                  <Listbox.Button
                    className="text-chariot-text-primary focus:ring-chariot-brand-primary cursor-not-allowed rounded border-none bg-transparent font-medium opacity-70 focus:ring-2 focus:outline-none"
                    aria-label="Change edge types"
                    data-testid={`edge-label-listbox-${edge.id}`}
                  >
                    {displayText}
                  </Listbox.Button>
                </Tooltip>
              ) : (
                <Listbox.Button
                  onClick={e => e.stopPropagation()}
                  className="text-chariot-text-primary inline-flex cursor-pointer items-center gap-1 rounded border-none bg-transparent font-medium hover:underline focus:ring-0 focus:outline-none"
                  aria-label="Change edge types"
                  data-testid={`edge-label-listbox-${edge.id}`}
                >
                  {displayText}
                  <ChevronDown className="h-4 w-4" />
                </Listbox.Button>
              )}

              <Listbox.Options className="bg-chariot-background-primary border-chariot-stroke absolute left-0 z-10 mt-1 max-h-60 w-64 overflow-y-auto rounded-md border shadow-lg">
                {allowedRelationships.map(rel => (
                  <Listbox.Option
                    key={rel.display}
                    value={rel.display}
                    className={({ active }) =>
                      `flex cursor-pointer items-center gap-2 px-3 py-2 ${
                        active ? 'bg-chariot-background-secondary' : ''
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <Check
                          className={`h-4 w-4 ${selected ? 'text-chariot-brand-primary visible' : 'invisible'}`}
                        />
                        <span className={selected ? 'font-semibold' : ''}>
                          {rel.display}
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>
        ) : (
          <div className="text-chariot-text-primary font-medium">
            {displayText}
          </div>
        )}
      </div>
      {hasFilters && (
        <div className="flex flex-col items-center gap-1">
          {isExpanded ? (
            <ChevronUp className="text-chariot-text-primary" />
          ) : (
            <ChevronDown className="text-chariot-text-primary" />
          )}
          <div className="text-chariot-text-secondary text-center text-xs">
            {filterCount === 0
              ? 'No filters'
              : `${filterCount} filter${filterCount !== 1 ? 's' : ''}`}
          </div>
        </div>
      )}
    </div>
  );
};
